import type { LiteralUnion, RequireExactlyOne, Tagged } from "type-fest";

import type { ScheduleCallbackType, SummaryResolution } from "@/shared-types";

import type { Task } from "./task";

export { AUDIO_TYPES, type AudioType, isAudioType } from "@expert/shared-types";

export type TaskCancelledReason = "CallCancelled" | "CallUnexpectedlyEnded" | "TechnicalIssues";

export type TaskCompletedReason =
    | "SessionEnded"
    | "CallbackInitiated"
    | "Transferred"
    | "TrainingCompleted"
    | "TechnicalIssues"
    | "ClosedByAutopilot";

type TaskWrapupReason = "ClosedByCustomer" | "ClosedByExpert" | "CustomerDisconnected";

export type TaskStatusReason = TaskWrapupReason | TaskCompletedReason | TaskCancelledReason;

export type ChannelKind = "voice" | "chat" | "sms" | "training";
export type TaskStatus = "pending" | "assigned" | "wrapping" | "completed" | "cancelled" | "missed";

export type AgentActivity = Tagged<string, "AgentActivity">;

export const mutableAgentAttributes = Object.freeze(["isPendingInactiveStatus", "pendingInactiveStatus"] as const);
export type MutableAgentAttribute = (typeof mutableAgentAttributes)[number];
export function isMutableAgentAttribute(value: unknown): value is MutableAgentAttribute {
    return (mutableAgentAttributes as readonly unknown[]).includes(value);
}

export type CallDirection = "inbound" | "outbound";

export interface HoldState {
    onHold: boolean;
    onHoldSince: number | null;
}

/** State that covers a new or existing callback for a voice task */
export interface CallbackState {
    originalTask: Task;
    sessionId: string;
    callbackType: ScheduleCallbackType;
    scheduledAt: number;
    scheduledFor: number;
    callbackDelay: number | null;
    callbackMDN: string;
}

/** Loggable view of a callback — drops the customer MDN and the full original task (customer PII). */
export function callbackStateToLog({ originalTask, callbackMDN: _callbackMDN, ...rest }: CallbackState) {
    return { ...rest, originalTaskId: originalTask.id };
}

//TODO: These can be more specific for tracking, such as tying them back to whether this was scheduled or was semi-ad hoc
export const OUTBOUND_CALL_KINDS = {
    CallbackNow: "CallbackNow", // Outbound call initiated during a session with a customer
    AdHocOutboundCall: "AdHocOutboundCall", // Outbound call not initiated during a session with a customer
} as const;

export type OutboundCallKind = (typeof OUTBOUND_CALL_KINDS)[keyof typeof OUTBOUND_CALL_KINDS];

export type WrappingStartReason = "TaskWrapping" | "CallbackCancelled" | "UnexpectedWrappingTask";
export type WrappingEndReason = "AgentTerminatedWrapping" | "WrappingTimeExpired" | "CallbackScheduled";
export interface WrappingState {
    startReason: WrappingStartReason;
    expirationTimestamp: number;
}

export interface SurveyMeta {
    resolution?: SummaryResolution;
    claimFiled?: boolean;
}

export const locales = Object.freeze(["en-us", "es-mx"] as const);
export type Locale = (typeof locales)[number];
export function isLocale(value: unknown): value is Locale {
    return (locales as readonly unknown[]).includes(value);
}

type KnownWhisper = "spanish";

export type Whisper = LiteralUnion<KnownWhisper, string>;

/**
 * Options for advancing the read status of messages.
 * Only one option should be provided at a time.
 */
interface ReadStatusOptions {
    /**
     * Advances the read status to this specific message index.
     */
    lastReadMessageIndex: number;

    /**
     * Marks all known messages as unread.
     */
    allMessagesUnread: true;

    /**
     * Marks all known messages as read (up to the latest known message index).
     */
    allMessagesRead: true;
}

/**
 * Union type to ensure only one read status advancement option is provided.
 */
export type AdvanceReadStatusOptions = RequireExactlyOne<
    ReadStatusOptions,
    "lastReadMessageIndex" | "allMessagesUnread" | "allMessagesRead"
>;

export interface IVRInfo {
    intent?: string;
    summary?: string;
    conversationalSummary?: string;
    status?: string;
    email?: string;
    device?: string;
    customerName?: string;
    orderNumber?: string;
    productLink?: string;
    productTitle?: string;
    brandPhoneNumber?: string;
    supportPhoneNumber?: string;
    accountId?: string;
    warmTransferMessage?: string;
    isWarmHandoffEnabled?: string;
    expertScript?: string;
    handoffRecordingRuntimeMs?: number;
    /** The Customer 360 (C360) interaction the IVR opened for this call, which Expert Workspace joins. */
    interactionId?: string;
    enableVmpSales?: string;
}

export type ServiceProvider = "connect" | "mock";
